import {Trans} from '@lingui/macro';
import _, {isNumber, keys, size, without} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {HandleThunkActionCreator} from 'react-redux';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  FrontClickEvent,
  HandlerOf,
  PredicateOf,
  RendererOf,
} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Dictionary} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {arrayToggle} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {PopoverTransferProps} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {usePrevious, useToggle} from '../../../srcCommon/helpers/hookHelpers';
import {buildIndexMap} from '../../../srcCommon/helpers/itemIndexHelpers';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {SelectedColumnSort} from '../../components/common/table/tableColumn';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {buildCancelTokenSource, CancelToken, CancelTokenSource} from '../../helpers/asyncCancelHelpers';
import {delayAsync} from '../../helpers/asyncHelpers';
import {
  buildTagSearchUrl,
  buildVisualSearchTags,
  buildVisualSearchTagsWithQuery,
  findParentTags,
  findTagChildren,
  getTagChildrenIds,
  SearchTagOptions,
  SearchTagSortsEnum,
  SearchTagStatusesEnum,
  VisualSearchTag,
} from '../../helpers/models/tagHelpers';
import {getSelectedIdsOnClick} from '../../helpers/selectionHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {SearchTagModel} from '../../models/api/searchTagModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {SettingsTagsUrl} from '../../models/url/settingsUrlModel';
import {
  InterfaceEventsEnum,
  SettingsTrackingTypesEnum,
  SingleTagCreationEntryPointsEnum,
  trackInterfaceEvent,
} from '../../services/tracking';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {nestTagsInsideNewParentTagAction} from '../../store/data/tags/tagBulkActions';
import {SettingsContentHeader} from '../common/settingsContentHeader';
import {
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsSidebar,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {useSettingsTagsContext} from './settingsTagsContext';
import {SettingsTagsHeaderContainer} from './settingsTagsHeaderContainer';
import {SettingsTagsListDialogs} from './settingsTagsListDialogs';
import {
  computeSelectedColumnSortFromTagSort,
  computeTagSortFromSelectedColumnSort,
  findParentsToFetchInSearchTags,
  settingsTagsListSortableColumns,
  trackSettingsTagsSearchEvent,
  useSettingsTagsListColumns,
} from './settingsTagsListHelpers';
import {SettingsTagsListSidebar} from './settingsTagsListSidebar';
import {SettingsTagsListTableDropdown} from './settingsTagsListTableDropdown';
import {SettingsTagsMultiSelectHeader} from './settingsTagsMultiSelectHeader';
import {SettingsTagsTableRow} from './settingsTagsTableRow';

/*
 * Constants.
 */

const archivedTagsHelpLink = 'https://help.frontapp.com/t/h4244c/understanding-tags#archive-tags';
const tagsHelpLink = 'https://help.frontapp.com/t/x16p1m/understanding-tags';

/*
 * Props.
 */

export interface SettingsTagsListProps {
  initialTagSort: SearchTagSortsEnum;
  isInArchivedView: boolean;
  isInPersonalNamespace: boolean;
  infoBoxId: string;
  tagsUrl: SettingsTagsUrl;
  company: CompanyModel;
  openParentTag: HandlerOf<number | undefined>;
  openSingleTag: HandlerOf<number>;
  openNewTag: (parentTagId: number | undefined, entryPoint: SingleTagCreationEntryPointsEnum) => void;
  findShouldDisplayInfoBox: PredicateOf<string>;
  dismissSettingsInfoBox: HandlerOf<string>;
  nestTagsInsideNewParentTag: HandleThunkActionCreator<typeof nestTagsInsideNewParentTagAction>;
}

/*
 * Component.
 */

export const SettingsTagsList: FC<SettingsTagsListProps> = (props) => {
  const theme = useUserTheme();
  const {
    tagsUrl,
    company,
    openParentTag,
    openSingleTag,
    openNewTag,
    findShouldDisplayInfoBox,
    dismissSettingsInfoBox,
    nestTagsInsideNewParentTag,
    isInArchivedView,
    infoBoxId,
    initialTagSort,
    isInPersonalNamespace,
  } = props;

  const [tagIdWithDropdownOpened, setTagIdWithDropdownOpened] = useState(-1);

  // Sidebar state.
  const [isSidebarOpen, toggleSidebar] = useToggle(!tagsUrl.tagId && findShouldDisplayInfoBox(infoBoxId));
  const onInfoBoxCloseClick = () => {
    dismissSettingsInfoBox(infoBoxId);
    toggleSidebar();
  };

  // Fetching state.
  const [searchQuery, setSearchQuery] = useState(tagsUrl.searchQuery ?? '');
  const [searchTags, setSearchTags] = useState<ReadonlyArray<SearchTagModel>>([]);
  const [offset, setOffset] = useState(0);
  const [isLoadingSubtags, setIsLoadingSubtags] = useState(false);
  const [statusMore, setStatusMore] = useState(AsyncMoreStatusesEnum.NONE);

  // Tags state.
  const [expandedTags, setExpandedTags] = useState<Dictionary<boolean>>({});
  const [selectedTagIds, setSelectedTagIds] = useState<ReadonlyArray<number>>([]);
  const isTagExpanded = useCallback(
    (tagId: number) => {
      if (searchQuery) {
        return true;
      }

      return Boolean(expandedTags[tagId]);
    },
    [expandedTags, searchQuery],
  );
  const tableItems = useMemo(() => {
    if (!searchTags) {
      return [];
    }

    if (searchQuery) {
      return buildVisualSearchTagsWithQuery(searchTags);
    }

    return buildVisualSearchTags(searchTags, isTagExpanded, tagsUrl.tagId, isInArchivedView);
  }, [searchTags, searchQuery, isTagExpanded, tagsUrl.tagId, isInArchivedView]);
  const shouldShowChevron = useMemo(() => {
    if (searchQuery) {
      return false;
    }

    return tableItems.some((tag) => getTagChildrenIds(tag.tag, isInArchivedView).length > 0);
  }, [isInArchivedView, searchQuery, tableItems]);
  const itemIndexes = useMemo(() => buildIndexMap(tableItems.map((t) => t.id)), [tableItems]);
  const tagsIdMap = useMemo(
    () =>
      _(searchTags)
        .flatten()
        .filter((t) => Boolean(isInArchivedView ? t.archivedAt : !t.archivedAt))
        .keyBy((t) => t.id)
        .value(),
    [searchTags, isInArchivedView],
  );
  const numberOfTagsInList = useMemo(() => size(tagsIdMap), [tagsIdMap]);

  // Fetching/cache context.
  const {requestTags, clearTagsCache} = useSettingsTagsContext();

  // Dialog states.
  const nestUnderDialogState = useState(false);
  const deleteDialogState = useState(false);
  const archiveDialogState = useState(false);
  const restoreDialogState = useState(false);
  const [, setIsNestUnderDialogOpen] = nestUnderDialogState;
  const [, setIsDeleteConfirmationOpen] = deleteDialogState;
  const [, setIsArchiveConfirmationOpen] = archiveDialogState;
  const [, setIsRestoreConfirmationOpen] = restoreDialogState;

  const [tagToActOn, setTagToActOn] = useState<TagModel | undefined>(undefined);
  const [tagSort, setTagSort] = useState<SearchTagSortsEnum | undefined>(initialTagSort);
  const bulkSelectedTags = useMemo(() => {
    if (tagToActOn) {
      return [tagToActOn];
    }

    return _(selectedTagIds)
      .map((id) => tagsIdMap[id])
      .compact()
      .value();
  }, [selectedTagIds, tagToActOn, tagsIdMap]);
  const previousParentsToFetch = usePrevious(
    findParentsToFetchInSearchTags(searchTags, tagsIdMap, isInArchivedView),
  );
  const parentsToFetch = findParentsToFetchInSearchTags(searchTags, tagsIdMap, isInArchivedView);

  // Initial fetching
  useInitialEffect(() => {
    fetchTags({
      newOffset: 0,
      newSort: initialTagSort,
      forceRefresh: true,
      newQuery: '',
    });
  });

  const fetchSubtagsBase = async (parentTagIds: number[], parentOffset = 0) => {
    const childrenSearchTags = await fetchTagsBase('', {
      parentTagIds,
      offset: parentOffset,
      status: isInArchivedView ? SearchTagStatusesEnum.ARCHIVED : SearchTagStatusesEnum.ACTIVE,
    });

    if (!childrenSearchTags) {
      return undefined;
    }

    setSearchTags((prev) => [...prev, ...childrenSearchTags.tags]);

    // Go through the newly fetched children and find out if their parents are selected
    const childrenIdsToSelect = _(childrenSearchTags.tags)
      .flattenDeep()
      .filter((t) => {
        const parents = findParentTags(tagsIdMap, t);

        return parents.length > 0 && parents.every((parentTag) => selectedTagIds.includes(parentTag.id));
      })
      .map((t) => t.id)
      .value();

    // If so, mark them as selected.
    if (childrenIdsToSelect.length) {
      setSelectedTagIds((prev) => _.uniq([...prev, ...childrenIdsToSelect]));
    }

    return childrenSearchTags;
  };

  const fetchAllSubtags = async (initialParentTagIds: number[]) => {
    setIsLoadingSubtags(true);
    let parentOffset = 0;
    let parentResult = await fetchSubtagsBase(initialParentTagIds);

    while (parentResult && parentResult.limit === parentResult.tags.length) {
      parentOffset += parentResult.limit;
      // eslint-disable-next-line no-await-in-loop
      parentResult = await fetchSubtagsBase(initialParentTagIds, parentOffset);
    }
    setIsLoadingSubtags(false);
  };

  // Everytime we fetch tags, fetch their potentially missing children
  useEffect(() => {
    if (
      searchQuery ||
      _.isEqual(parentsToFetch, previousParentsToFetch) ||
      parentsToFetch.length === 0 ||
      isLoadingSubtags
    ) {
      return;
    }

    fetchAllSubtags(parentsToFetch);
  });

  const hasAllSelected = useMemo(
    () => selectedTagIds.length === numberOfTagsInList,
    [selectedTagIds.length, numberOfTagsInList],
  );

  // Cancellation.
  const loadingStatusCancelTokenSourceRef = useRef<CancelTokenSource>();
  function maybeCancelLoadingStatusGracePeriod() {
    const currentCancelTokenSource = loadingStatusCancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  }

  // Grace period.
  const setStatusAfterGracePeriodAsync = async (cancelToken: CancelToken, delay = 600) => {
    try {
      await delayAsync(delay, cancelToken);
      setStatusMore(AsyncMoreStatusesEnum.LOADING);
    } catch (e) {
      // Nothing to do.
    }
  };

  // Fetching methods.
  const fetchTagsBase = async (
    query?: string,
    options?: Pick<
      Partial<SearchTagOptions>,
      'shouldReturnParentsOnly' | 'parentTagIds' | 'offset' | 'status' | 'sort'
    > & {
      forceRefresh?: boolean;
      canBeCancelled?: boolean;
    },
  ) => {
    const finalOptions: SearchTagOptions = {
      isInSettingsContext: true,
      namespaces: [tagsUrl.namespace],
      behaviors: [TagBehaviorsEnum.CAN_UPDATE],
      offset: options?.offset ?? offset,
      parentTagIds: _(options?.parentTagIds || [])
        .push(tagsUrl.tagId || 0)
        .uniq()
        .compact()
        .value(),
      shouldReturnParentsOnly: options?.shouldReturnParentsOnly,
      status: options?.status,
      sort: options?.sort ?? tagSort,
    };

    const url = buildTagSearchUrl(company, query || '', finalOptions);

    return await requestTags(url, options?.canBeCancelled ?? false, options?.forceRefresh);
  };
  const fetchTags = async (options: {
    newQuery?: string;
    newOffset?: number;
    forceRefresh?: boolean;
    newSort?: SearchTagSortsEnum;
    canBeCancelled?: boolean;
  }) => {
    try {
      maybeCancelLoadingStatusGracePeriod();
      const {newQuery, forceRefresh, newOffset, newSort, canBeCancelled} = options;

      const loadingStatusCancelTokenSource = buildCancelTokenSource();
      loadingStatusCancelTokenSourceRef.current = loadingStatusCancelTokenSource;

      if (isNumber(newOffset)) {
        setStatusAfterGracePeriodAsync(loadingStatusCancelTokenSource.token, newSort ? 200 : 0);
      }

      const tagSearch = await fetchTagsBase(newQuery ?? searchQuery, {
        shouldReturnParentsOnly: !tagsUrl.tagId && !(newQuery ?? searchQuery),
        offset: newOffset,
        forceRefresh,
        status: isInArchivedView ? SearchTagStatusesEnum.ARCHIVED : SearchTagStatusesEnum.ACTIVE,
        sort: newSort,
        canBeCancelled,
      });
      loadingStatusCancelTokenSource.cancel();

      if (!tagSearch) {
        setStatusMore(AsyncMoreStatusesEnum.ERROR);
        return;
      }

      if (tagSearch.limit === tagSearch.tags.length) {
        setStatusMore(AsyncMoreStatusesEnum.HAS_MORE);
        if (newQuery || forceRefresh) {
          setOffset(tagSearch.limit);
        } else {
          setOffset((prev) => prev + tagSearch.limit);
        }
      } else {
        setStatusMore(AsyncMoreStatusesEnum.ALL_LOADED);
      }

      if (newQuery || forceRefresh) {
        setSearchTags(tagSearch.tags);
      } else {
        setSearchTags((prev) => [...prev, ...tagSearch.tags]);
        setSelectedTagIds((prev) => {
          // If we're loading the initial page or we didn't select everything, nothing to do
          if (prev.length < 1 || !hasAllSelected) {
            return prev;
          }

          // Otherwise, grab the newly fetched tags and select them
          return _.uniq([
            ...prev,
            ..._(tagSearch.tags)
              .flatten()
              .map((t) => t.id)
              .value(),
          ]);
        });
      }
    } catch (e) {
      console.error(e);
      setStatusMore(AsyncMoreStatusesEnum.ERROR);
    }
  };

  // Reset.
  const resetState = (newTagSort?: SearchTagSortsEnum) => {
    setStatusMore(AsyncMoreStatusesEnum.NONE);
    setIsDeleteConfirmationOpen(false);
    setIsArchiveConfirmationOpen(false);
    setIsRestoreConfirmationOpen(false);
    setIsNestUnderDialogOpen(false);
    setSelectedTagIds([]);
    setExpandedTags({});
    setTagToActOn(undefined);
    if (newTagSort) {
      setTagSort(newTagSort);
    }

    setOffset(0);
    setSearchTags([]);
    setSearchQuery('');
    clearTagsCache();
    fetchTags({
      forceRefresh: true,
      newQuery: '',
      newOffset: 0,
      newSort: newTagSort,
      canBeCancelled: Boolean(newTagSort),
    });
  };

  const onSearchQueryChange = (newQuery: string) => {
    if (!newQuery) {
      resetState();
      return;
    }
    trackSettingsTagsSearchEvent(tagsUrl);
    fetchTags({
      newQuery,
      newOffset: newQuery !== searchQuery ? 0 : undefined,
      canBeCancelled: true,
    });
    setSearchQuery(newQuery);
    setSelectedTagIds([]);
    setTagToActOn(undefined);
  };

  const toggleSelectedTag = (tag: TagModel, e?: FrontClickEvent) => {
    const childrenIds = findTagChildren(tagsIdMap, tag, isInArchivedView).map((t) => t.id);
    const parentIds = findParentTags(tagsIdMap, tag).map((t) => t.id);

    setTagToActOn(undefined);

    setSelectedTagIds((prevSelected) => {
      function getInitialNewSelected() {
        if (!e || (!e?.shiftKey && !e?.metaKey)) {
          return arrayToggle(prevSelected, tag.id);
        }

        if (prevSelected.length === 1 && tag.id === prevSelected[0]) {
          return [];
        }

        return getSelectedIdsOnClick(
          prevSelected,
          _(tableItems)
            .map((t) => t.id)
            .value(),
          itemIndexes,
          tag.id,
          e,
        );
      }

      let newSelected = getInitialNewSelected();

      // If we're inside a search, we don't want to potentially select the tree.
      if (searchQuery) {
        return newSelected;
      }

      const parentGotSelected = !prevSelected.includes(tag.id) && newSelected.includes(tag.id);
      const parentGotUnselected = prevSelected.includes(tag.id) && !newSelected.includes(tag.id);

      childrenIds.forEach((childId) => {
        if (
          (newSelected.includes(childId) && parentGotUnselected) ||
          (!newSelected.includes(childId) && parentGotSelected)
        ) {
          newSelected = arrayToggle(newSelected, childId);
        }
      });

      // If our tag has been deselected, we also need to deselect its parents
      if (!newSelected.includes(tag.id)) {
        const parentsToDeselect = parentIds.filter((id) => newSelected.includes(id));
        newSelected = without(newSelected, ...parentsToDeselect);
      }

      return _(newSelected)
        .uniq()
        .sortBy((id) => itemIndexes[id])
        .value();
    });
  };

  const renderRightHeader = () => {
    const {tagId} = tagsUrl;
    if (tagId) {
      return (
        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={() => openSingleTag(tagId)}
        >
          <Trans id="settings-edittag">Edit tag</Trans>
        </Button>
      );
    }

    return (
      <IconButton
        iconName={isSidebarOpen ? 'infoFilled' : 'info'}
        label=""
        onClick={toggleSidebar}
        color={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
        hoverColor={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
      />
    );
  };

  const columns = useSettingsTagsListColumns(isInArchivedView, isInPersonalNamespace);

  const onInfoBoxFooterClick = () => {
    if (isInArchivedView) {
      openInBrowser(archivedTagsHelpLink);
    } else {
      openInBrowser(tagsHelpLink);
    }
  };

  const renderHeaderButton = (buttonProps: React.ComponentProps<typeof Button>) => (
    <Button
      {...buttonProps}
      variant={ButtonVariantsEnum.PRIMARY}
      onClick={() => openNewTag(undefined, SingleTagCreationEntryPointsEnum.SETTINGS_MENU)}
    >
      <Trans id="settingstags-addatag">Add a tag</Trans>
    </Button>
  );
  const renderActionsDropdown: RendererOf<{
    modalProps: PopoverTransferProps;
    value: TagModel | undefined;
  }> = ({modalProps, value}) => (
    <SettingsTagsListTableDropdown
      anchor={modalProps.anchor}
      anchorWidth={modalProps.anchorWidth}
      onRequestClose={modalProps.onRequestClose}
      wrap={modalProps.wrap}
      isInArchivedView={isInArchivedView}
      onEditTagSelect={() => {
        openSingleTag(value?.id || 0);
      }}
      onNestUnderSelect={() => {
        setTagToActOn(value);
        setIsNestUnderDialogOpen(true);
      }}
      onAddSubtagSelect={() => {
        openNewTag(value?.id, SingleTagCreationEntryPointsEnum.THREE_DOT_SELECTION);
      }}
      onDeleteSelect={() => {
        setTagToActOn(value);
        setIsDeleteConfirmationOpen(true);
      }}
      onArchiveSelect={() => {
        setTagToActOn(value);
        setIsArchiveConfirmationOpen(true);
      }}
      onRestoreSelect={() => {
        setTagToActOn(value);
        setIsRestoreConfirmationOpen(true);
      }}
    />
  );

  const onTagClick = (event: FrontClickEvent, onNameCell: boolean, tag: TagModel) => {
    if (!onNameCell && selectedTagIds.length === 0) {
      return;
    }

    if (isInArchivedView && (selectedTagIds.length === 0 || Boolean(tag.parentTagId)) && !event.shiftKey) {
      return;
    }

    if (selectedTagIds.length > 0 || event.shiftKey || event.metaKey) {
      toggleSelectedTag(tag, event);
      return;
    }

    if (getTagChildrenIds(tag, isInArchivedView).length > 0) {
      openParentTag(tag.id);
    } else {
      openSingleTag(tag.id);
    }
  };

  const renderRow: VirtualTableRowRenderer<VisualSearchTag> = (item, options, siblingData) => (
    <SettingsTagsTableRow
      isNextSiblingSelected={selectedTagIds.includes(siblingData?.next?.id ?? -1)}
      isPreviousSiblingSelected={selectedTagIds.includes(siblingData?.previous?.id ?? -1)}
      isSelected={selectedTagIds.includes(item.id)}
      isInMultiSelectMode={selectedTagIds.length > 0}
      onCheckBoxToggle={(e) => toggleSelectedTag(item.tag, e)}
      onNameCellClick={(e) => onTagClick(e, true, item.tag)}
      onCellClick={(e) => onTagClick(e, false, item.tag)}
      options={options}
      tableColumns={columns}
      tag={item.tag}
      searchTagsIdMap={tagsIdMap}
      isInArchivedView={isInArchivedView}
      isInSearch={Boolean(searchQuery)}
      hasChevron={shouldShowChevron}
      hasHover={item.id === tagIdWithDropdownOpened}
      isExpanded={isTagExpanded(item.tag.id)}
      onChevronClick={() =>
        setExpandedTags((prevExpanded) => ({
          ...prevExpanded,
          [item.tag.id]: !prevExpanded[item.tag.id],
        }))
      }
      onDeleteSingleTagClick={(tag) => {
        setTagToActOn(tag);
        setIsDeleteConfirmationOpen(true);
      }}
      selectedTagIds={selectedTagIds}
      renderActionsDropdown={renderActionsDropdown}
      onActionsDropdownOpened={(isOpen) => setTagIdWithDropdownOpened(isOpen ? item.id : -1)}
    />
  );

  const onColumnClick = (col: SelectedColumnSort) => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_SORT_BY, {
      settings_subpage: tagsUrl.tabPage,
      settings_type: isInPersonalNamespace
        ? SettingsTrackingTypesEnum.TEAM
        : SettingsTrackingTypesEnum.PERSONAL,
      criteria: computeTagSortFromSelectedColumnSort(col),
      click_location: 'table_column',
    });
    resetState(computeTagSortFromSelectedColumnSort(col));
  };

  return (
    <StyledSettingsWrapper>
      <SettingsTagsListDialogs
        bulkSelectedTags={bulkSelectedTags}
        company={company}
        isInArchivedView={isInArchivedView}
        searchQuery={searchQuery}
        setTagToActOn={setTagToActOn}
        tagsIdMap={tagsIdMap}
        tagsUrl={tagsUrl}
        tagToActOn={tagToActOn}
        resetState={resetState}
        expandedTags={expandedTags}
        selectedTagIds={selectedTagIds}
        numberOfTagsInList={numberOfTagsInList}
        nestTagsInsideNewParentTag={nestTagsInsideNewParentTag}
        nestUnderDialogState={nestUnderDialogState}
        deleteDialogState={deleteDialogState}
        archiveDialogState={archiveDialogState}
        restoreDialogState={restoreDialogState}
      />
      <SettingsTagsHeaderContainer
        tagsUrl={tagsUrl}
        tagId={tagsUrl.tagId}
        openParentTag={openParentTag}
        right={renderRightHeader()}
      />

      <StyledSettingsMain>
        <StyledSettingsContent>
          <SettingsContentHeader
            searchInputValue={searchQuery}
            onSearchInput={onSearchQueryChange}
            onExitClick={() => resetState()}
            renderButtons={!isInArchivedView ? renderHeaderButton : undefined}
          />
          <StaticVirtualTable<VisualSearchTag>
            items={tableItems}
            columns={columns}
            sortedColumn={computeSelectedColumnSortFromTagSort(tagSort)}
            sortableColumns={settingsTagsListSortableColumns}
            renderRow={renderRow}
            moreStatuses={{
              [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
              [ScrollDirectionsEnum.BOTTOM]: statusMore,
            }}
            hasData={statusMore !== AsyncMoreStatusesEnum.NONE}
            shouldRenderEmptyState={
              statusMore === AsyncMoreStatusesEnum.ALL_LOADED && tableItems.length === 0
            }
            onSortUpdate={onColumnClick}
            onLoadMore={() => fetchTags({newQuery: searchQuery})}
            renderMultiSelectHeader={(onMultiSelectHeaderChange) => (
              <SettingsTagsMultiSelectHeader
                onMultiSelectChange={onMultiSelectHeaderChange}
                selectedTagIds={selectedTagIds}
                numberOfTagsInList={numberOfTagsInList}
                onNestClick={() => setIsNestUnderDialogOpen(true)}
                onDeleteClick={() => setIsDeleteConfirmationOpen(true)}
                onArchiveClick={() => setIsArchiveConfirmationOpen(true)}
                onRestoreClick={() => {
                  setIsRestoreConfirmationOpen(true);
                }}
                onCancelClick={() => setSelectedTagIds([])}
                isInArchivedView={isInArchivedView}
              />
            )}
            shouldAllowMultiSelect
            onMultiSelectHeaderChange={(isSelectingAll) =>
              setSelectedTagIds(isSelectingAll ? keys(tagsIdMap).map((n) => Number(n)) : [])
            }
            isInMultiSelectMode={selectedTagIds.length > 0}
          />
        </StyledSettingsContent>
        {isSidebarOpen && (
          <StyledSettingsSidebar>
            <SettingsTagsListSidebar
              isInArchivedView={isInArchivedView}
              namespace={tagsUrl.namespace}
              onCloseClick={onInfoBoxCloseClick}
              onFooterClick={onInfoBoxFooterClick}
            />
          </StyledSettingsSidebar>
        )}
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};
